/* eslint angular/no-private-call: 0 */
/** Calling private $$state to get all the important attributes. */

/**
 * @typedef {Object} SubpagesService
 *
 * @property {uirouter.StateDeclaration[]} currentSubpages
 * @property {function} getSubpageInfoByStateId
 * @property {function} getCurrentSubpageInfo
 * @property {function} getCurrentSubpageSiblings
 * @property {function} setSubpagesFromIds
 * @property {function} setSubpagesFromChildren
 * @property {function} isPostgradSubpage
 * @property {function} isDegreeSubpage
 * @property {function} isPostgradAreaSubpage
 * @property {function} isSpecialisation
 * @property {function} isSubjectSelectable
 * @property {function} isProgrammeSelectable
 * @property {function} isPostgradProfessionalSubpage
 * @property {function} isPostgradAcademicSubpage
 * @property {function} isPageNotFound
 * @property {function} setSelectableAttribute
 * @property {function} getCurrentSubpageContentId
 * @property {function} getParentState
 */


/**
 * @typedef {Object} SubpageData
 *
 * @property {string} contentId
 * @property {{iconClass: string, label: string, title: string}} menuItem
 * @property {{label: string}} stepButton
 */


(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'SubpagesService', [ '$state', '$uiRouter', 'LoggerFactory', 'TEXTS', 'CONFIG', SubpagesService ]);


    /**
     * A singleton providing various helper methods for Services,
     * Controllers and Views handling rendering of tabbed sub-pages.
     *
     * @param {uirouter.State} $state
     * @param {uirouter.UiRouter} $uiRouter
     * @param {LoggerFactory} LoggerFactory
     * @param {Object} TEXTS
     * @param {AppConfig} CONFIG
     *
     * @returns {SubpagesService}
     */
    function SubpagesService(
        $state,
        $uiRouter,
        LoggerFactory,
        TEXTS,
        CONFIG
    ){





        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'SubpagesService', false );


        /** @constant */
        var SUBPAGE_DATA_ATTRIBUTE = 'subpage';


        /**
         * Information about available sub-pages
         *
         * @type {uirouter.StateDeclaration[]}
         */
        var currentSubpages = [];

        /** @type {uirouter.StateDeclaration[]} */
        var statesList = $uiRouter.stateRegistry.states;

        /** @type {String} */
        var selectabilityDimension;


        /** @type {SubpagesService} */
        var service = {
            // Members
            currentSubpages:           currentSubpages,

            // Methods
            getSubpageInfoByStateId:   getSubpageInfoByStateId,
            getCurrentSubpageInfo:     getCurrentSubpageInfo,
            getCurrentSubpageSiblings: getCurrentSubpageSiblings,
            getCurrentSubpageContentId: getCurrentSubpageContentId,

            setSubpagesFromIds:        setSubpagesFromIds,
            setSubpagesFromChildren:   setSubpagesFromChildren,

            setSelectableAttribute:     setSelectableAttribute,
            getSelectableAttribute:     getSelectableAttribute,
            isSubjectSelectable:        isSubjectSelectable,
            isProgrammeSelectable:      isProgrammeSelectable,
            isSpecialisationSelectable: isSpecialisationSelectable,
            isSpecialisation:           isSpecialisation,

            isPostgradSubpage:             isPostgradSubpage,
            isGraduateQualSubpage:         isGraduateQualSubpage,
            isPostgradAcademicSubpage:     isPostgradAcademicSubpage,
            isPostgradProfessionalSubpage: isPostgradProfessionalSubpage,
            isDegreeSubpage:               isDegreeSubpage,
            isPostgradAreaSubpage:         isPostgradAreaSubpage,
            isPageNotFound:                isPageNotFound,

            getParentState:                getParentState,
            getStateIdFromSectionId:       getStateIdFromSectionId
        };





        /* ===== CONSTRUCTOR ================================================ */


        /** @constructor */
        (function init(){
            decorateStatesWithSubpageData();
        })();



        ////////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////////



        /* ===== PUBLIC METHODS ============================================= */


        /**
         * Takes an array of subpage IDs and initialises one by one.
         *
         * Note: Order of those subpages is defined by the declaration order
         * within core/routes.js!
         *
         * @param {string[]} subpagesIds Set of IDs based on which subpage
         *                               states will be initialised.
         */
        function setSubpagesFromIds( subpagesIds ) {
            var parentStateName = $state.current.parent ? $state.current.parent.name + '#' : '',
            newSubpages = [], appSubpageId;

            newSubpages = subpagesIds.map( function( subpageId ) {
                // Update the IDs to reflect states' IDs as defined in CONFIG.STATES.*
                appSubpageId = parentStateName + subpageId.toLowerCase();

                if ( statesList.hasOwnProperty( appSubpageId ) ){
                    return statesList[ appSubpageId ];
                } else {
                    logger.info( '$state `%s` does not exist, so it will not be added to the list of available subpages.', appSubpageId );
                }
            });

            service.currentSubpages = newSubpages;
        }


        /**
         * @param {string} stateId
         *
         * @returns {SubpageData}
         */
        function getSubpageInfoByStateId( stateId ) {
            if ( statesList.hasOwnProperty( stateId ) ){
                return statesList[ stateId ].data[ SUBPAGE_DATA_ATTRIBUTE ];
            } else {
                logger.error( "Cannot retrieve a subpage information from non-existing $state `%s`.", stateId );
                return null;
            }
        }


        /**
         * @param {uirouter.StateDeclaration[]} newAvailableSubpages
         *
         * @returns {{previousSibling:uirouter.StateDeclaration, nextSibling:uirouter.StateDeclaration}}
         */
        function getCurrentSubpageSiblings( newAvailableSubpages ) {
            var currentSubpagesSiblings = null,
            availableSubpages = newAvailableSubpages || currentSubpages;

            availableSubpages.some( function( subpage, index ){
                if ( subpage.name === $state.current.name ){

                    currentSubpagesSiblings = {
                        'previousSibling': ( index == 0 ? null : availableSubpages[ index - 1 ] ),
                        'nextSibling':     ( index >= ( availableSubpages.length - 1 ) ? null : availableSubpages[ index + 1 ] )
                    };

                    return true;
                }
            });

            if ( !currentSubpagesSiblings ){
                logger.error( "State `%s` does not seem to have any siblings pages.", $state.current.name, availableSubpages );
            }

            return currentSubpagesSiblings;
        }


        /**
         * @returns {uirouter.StateDeclaration}
         */
        function getParentState() {
            return $state.current.parent;
        }


        /**
         * @returns {SubpageData}
         */
        function getCurrentSubpageInfo() {
            return $state.current.data[ SUBPAGE_DATA_ATTRIBUTE ];
        }


        /**
         *  @returns {String}
         */
        function getCurrentSubpageContentId() {
            return getCurrentSubpageInfo().contentId;
        }


        /**
         * @returns {boolean}
         */
        function isPostgradSubpage() {
            return ( $state.current.hasOwnProperty( 'parent' ) && $state.current.parent.name === CONFIG.STATES.POSTGRAD );
        }

        /**
         * @returns {boolean}
         */
        function isGraduateQualSubpage() {
            if(isPostgradSubpage()) return ($state.params.hasOwnProperty('qualType') && $state.params.qualType == CONFIG.ROUTES.GRADUATE_QUALS);
            return false;
        }

        /**
         * @returns {boolean}
         */
        function isDegreeSubpage() {
            return ( $state.current.hasOwnProperty( 'parent' ) && $state.current.parent.name === CONFIG.STATES.DEGREE );
        }


        /**
         * @returns {boolean}
         */
        function isPostgradAreaSubpage() {
            return ( $state.current.hasOwnProperty( 'parent' ) && $state.current.parent.name === CONFIG.STATES.POSTGRAD_AREA );
        }

        function isSpecialisation () {
            return ( $state.current.hasOwnProperty( 'parent' ) && $state.current.parent.name === CONFIG.STATES.SPECIALISATION_AREA );
        }

        /**
         * @returns {boolean}
         */
        function isPostgradAcademicSubpage() {
            return isPostgradSubpage() && isSubjectSelectable();
        }

        /**
         * @returns {boolean}
         */
        function isPostgradProfessionalSubpage() {
            return isPostgradSubpage() && isProgrammeSelectable();
        }


        /** @param {string} selectableAttribute */
        function setSelectableAttribute( selectableAttribute ) {
            selectabilityDimension = selectableAttribute;
        }


        /** @returns {string} */
        function getSelectableAttribute() {
            return selectabilityDimension;
        }


        /** @returns {boolean} */
        function isSubjectSelectable() {
            return selectabilityDimension === CONFIG.PARAMS.SUBJECT || selectabilityDimension === CONFIG.PARAMS.SPECIALISATION;
        }

        /** @returns {boolean} */
        function isProgrammeSelectable() {
            return selectabilityDimension === CONFIG.PARAMS.PROGRAMME || selectabilityDimension === CONFIG.PARAMS.SPECIALISATION;
        }

        /** @returns {boolean} */
        function isSpecialisationSelectable() {
            return selectabilityDimension === CONFIG.PARAMS.SPECIALISATION;
        }

        function isPageNotFound() {
            return ( $state.current.name === CONFIG.STATES.SERVER_NOT_FOUND || $state.current.name === CONFIG.STATES.NOT_FOUND );
        }


        function setSubpagesFromChildren() {
            service.currentSubpages = getAllChildrenStates();
        }





        /* ===== PRIVATE METHODS ============================================ */


        /**
         * @returns {uirouter.StateDeclaration[]}
         */
        function getAllChildrenStates(){
            var currentState = $state.current,
            allChildren = currentState.parent ? currentState.parent.children : currentState.children;

            // Transform StateDeclaration into $state
            return allChildren.map( function( childStateDeclaration ) {
                return childStateDeclaration.$$state();
            });
        }

        function decorateStatesWithSubpageData() {

            /** `Areas of study` tabs */
            addSubpageDataToState( CONFIG.STATES.STUDY_AREAS.ABOUT, {
                menuItem: {
                    iconClass:  'list',
                    label:      TEXTS.AREA.MENU.ABOUT_LABEL,
                    title:      TEXTS.AREA.MENU.ABOUT_TITLE
                },
                stepButton: {
                    label:      TEXTS.AREA.BUTTONS.STEP_ABOUT_LABEL
                },
                meta: {
                    title:          TEXTS.AREA.META_TITLE.ABOUT,
                    description:    TEXTS.AREA.META_DESCRIPTION.ABOUT
                },
                contentId: 'aboutBlocks',
                id:        'overview'
            });

            addSubpageDataToState( CONFIG.STATES.STUDY_AREAS.STUDY, {
                menuItem: {
                    iconClass:  'study-cap',
                    label:      TEXTS.AREA.MENU.STUDY_LABEL,
                    title:      TEXTS.AREA.MENU.STUDY_TITLE
                },
                stepButton: {
                    label:      TEXTS.AREA.BUTTONS.STEP_STUDY_LABEL
                },
                meta: {
                    title:          TEXTS.AREA.META_TITLE.STUDY,
                    description:    TEXTS.AREA.META_DESCRIPTION.STUDY
                },
                contentId: 'studyBlocks',
                id:        'study'
            });

            addSubpageDataToState( CONFIG.STATES.STUDY_AREAS.PEOPLE, {
                menuItem: {
                    iconClass:  'talk-bubble',
                    label:      TEXTS.AREA.MENU.PEOPLE_LABEL,
                    title:      TEXTS.AREA.MENU.PEOPLE_TITLE
                },
                stepButton: {
                    label:      TEXTS.AREA.BUTTONS.STEP_PEOPLE_LABEL
                },
                meta: {
                    title:          TEXTS.AREA.META_TITLE.PEOPLE,
                    description:    TEXTS.AREA.META_DESCRIPTION.PEOPLE
                },
                contentId: 'peopleBlocks',
                id:        'people'
            });

            /** `Undergraduate degrees` tabs */
            addSubpageDataToState( CONFIG.STATES.DEGREES.ABOUT, {
                menuItem: {
                    iconClass:  'list',
                    label:      TEXTS.DEGREE.MENU.ABOUT_LABEL,
                    title:      TEXTS.DEGREE.MENU.ABOUT_TITLE
                },
                stepButton: {
                    label:      TEXTS.DEGREE.BUTTONS.STEP_ABOUT_LABEL
                },
                meta: {
                    title:          TEXTS.DEGREE.META_TITLE.ABOUT,
                    description:    TEXTS.DEGREE.META_DESCRIPTION.ABOUT
                },
                contentId: 'aboutBlocks',
                id:        'overview'
            });

            addSubpageDataToState( CONFIG.STATES.DEGREES.REQUIREMENTS, {
                menuItem: {
                    iconClass:  'ok',
                    label:      TEXTS.DEGREE.MENU.REQUIREMENTS_LABEL,
                    title:      TEXTS.DEGREE.MENU.REQUIREMENTS_TITLE
                },
                stepButton: {
                    label:      TEXTS.DEGREE.BUTTONS.STEP_REQUIREMENTS_LABEL
                },
                meta: {
                    title:          TEXTS.DEGREE.META_TITLE.REQUIREMENTS,
                    description:    TEXTS.DEGREE.META_DESCRIPTION.REQUIREMENTS
                },
                contentId: 'requirementsBlocks',
                id:        'requirements'
            });

            addSubpageDataToState( CONFIG.STATES.DEGREES.APPLY, {
                menuItem: {
                    iconClass:  'vcard',
                    label:      TEXTS.DEGREE.MENU.APPLY_LABEL,
                    title:      TEXTS.DEGREE.MENU.APPLY_TITLE
                },
                stepButton: {
                    label:      TEXTS.DEGREE.BUTTONS.STEP_APPLY_LABEL
                },
                meta: {
                    title:       TEXTS.DEGREE.META_TITLE.APPLY,
                    description: TEXTS.DEGREE.META_DESCRIPTION.APPLY
                },
                contentId: 'applyBlocks',
                id:        'apply'
            });

            /** `Postgraduate areas of study` tabs */
            addSubpageDataToState( CONFIG.STATES.POSTGRAD_AREAS.ABOUT, {
                menuItem: {
                    iconClass:  'list',
                    label:      TEXTS.POSTGRAD_AREAS.MENU.ABOUT_LABEL,
                    title:      TEXTS.POSTGRAD_AREAS.MENU.ABOUT_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD_AREAS.BUTTONS.STEP_ABOUT_LABEL,
                },
                meta: {
                    title:          TEXTS.POSTGRAD_AREAS.META_TITLE.ABOUT,
                    description:    TEXTS.POSTGRAD_AREAS.META_DESCRIPTION.ABOUT
                },
                contentId: 'AboutBlocks',
                id:        'overview'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRAD_AREAS.QUALIFICATIONS, {
                menuItem: {
                    iconClass:  'study-cap',
                    label:      TEXTS.POSTGRAD_AREAS.MENU.QUALIFICATIONS_LABEL,
                    title:      TEXTS.POSTGRAD_AREAS.MENU.QUALIFICATIONS_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD_AREAS.BUTTONS.STEP_QUALIFICATIONS_LABEL
                },
                meta: {
                    title:       TEXTS.POSTGRAD_AREAS.META_TITLE.QUALIFICATIONS,
                    description: TEXTS.POSTGRAD_AREAS.META_DESCRIPTION.QUALIFICATIONS
                },
                contentId: 'QualificationsBlocks',
                id:        'qualifications'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRAD_AREAS.PEOPLE, {
                menuItem: {
                    iconClass:  'group',
                    label:      TEXTS.POSTGRAD_AREAS.MENU.PEOPLE_LABEL,
                    title:      TEXTS.POSTGRAD_AREAS.MENU.PEOPLE_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD_AREAS.BUTTONS.STEP_PEOPLE_LABEL
                },
                meta: {
                    title:          TEXTS.POSTGRAD_AREAS.META_TITLE.PEOPLE,
                    description:    TEXTS.POSTGRAD_AREAS.META_DESCRIPTION.PEOPLE
                },
                contentId: 'PeopleBlocks',
                id:        'people'
            });

            /** Postgraduate and graduate qualifications. */
            addSubpageDataToState( CONFIG.STATES.POSTGRADS.ABOUT, {
                menuItem: {
                    iconClass:  'list',
                    label:      TEXTS.POSTGRAD.MENU.ABOUT_LABEL,
                    title:      TEXTS.POSTGRAD.MENU.ABOUT_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD.BUTTONS.STEP_ABOUT_LABEL
                },
                meta: {
                    title:          TEXTS.POSTGRAD.META_TITLE.ABOUT,
                    description:    TEXTS.POSTGRAD.META_DESCRIPTION.ABOUT
                },
                contentId: 'AboutBlocks',
                id:        'overview'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRADS.STUDY, {
                menuItem: {
                    iconClass:  'study-cap',
                    label:      TEXTS.POSTGRAD.MENU.STUDY_LABEL,
                    title:      TEXTS.POSTGRAD.MENU.STUDY_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD.BUTTONS.STEP_STUDY_LABEL
                },
                meta: {
                    title: TEXTS.POSTGRAD.META_TITLE.STUDY,
                    description:    TEXTS.POSTGRAD.META_DESCRIPTION.STUDY
                },
                contentId: 'StudyBlocks',
                id:        'study'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRADS.REQUIREMENTS, {
                menuItem: {
                    iconClass:  'ok',
                    label:      TEXTS.POSTGRAD.MENU.REQUIREMENTS_LABEL,
                    title:      TEXTS.POSTGRAD.MENU.REQUIREMENTS_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD.BUTTONS.STEP_REQUIREMENTS_LABEL
                },
                meta: {
                    title:          TEXTS.POSTGRAD.META_TITLE.REQUIREMENTS,
                    description:    TEXTS.POSTGRAD.META_DESCRIPTION.REQUIREMENTS
                },
                contentId: 'RequirementsBlocks',
                id:        'requirements'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRADS.PEOPLE, {
                menuItem: {
                    iconClass:  'group',
                    label:      TEXTS.POSTGRAD.MENU.PEOPLE_LABEL,
                    title:      TEXTS.POSTGRAD.MENU.PEOPLE_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD.BUTTONS.STEP_PEOPLE_LABEL
                },
                meta: {
                    title:          TEXTS.POSTGRAD.META_TITLE.PEOPLE,
                    description:    TEXTS.POSTGRAD.META_DESCRIPTION.PEOPLE
                },
                contentId: 'PeopleBlocks',
                id:        'people'
            });

            addSubpageDataToState( CONFIG.STATES.POSTGRADS.APPLY, {
                menuItem: {
                    iconClass:  'phone',
                    label:      TEXTS.POSTGRAD.MENU.APPLY_LABEL,
                    title:      TEXTS.POSTGRAD.MENU.APPLY_TITLE
                },
                stepButton: {
                    label:      TEXTS.POSTGRAD.BUTTONS.STEP_APPLY_LABEL
                },
                meta: {
                    title:          TEXTS.POSTGRAD.META_TITLE.APPLY,
                    description:    TEXTS.POSTGRAD.META_DESCRIPTION.APPLY
                },
                contentId: 'ApplyBlocks',
                id:        'apply',
            });
        }


        /**
         * @param {string} stateId Identifier defined in CONFIG.STATES.*
         * @param {SubpageData} subpageDataObject
         */
        function addSubpageDataToState( stateId, subpageDataObject ){
            if ( statesList.hasOwnProperty( stateId ) ){
                var state = statesList[ stateId ];
                if ( !state.self.data ) state.self.data = {};
                if ( !state.data ) state.data = {};

                state.self.data[ SUBPAGE_DATA_ATTRIBUTE ]   = subpageDataObject;
                state.data[ SUBPAGE_DATA_ATTRIBUTE ]        = subpageDataObject;
            } else {
                logger.error( 'Cannot add custom subpage data to the $state `%s` because $state with this ID does not exist.', stateId, subpageDataObject  );
            }
        }


        /**
         * Retrieves state ID from `currentSubpages` based on given
         * section ID (~ content ID).
         *
         * @param {string} sectionId
         *
         * @returns {string}
         */
        function getStateIdFromSectionId( sectionId ) {
            var foundStateId = undefined;

            service.currentSubpages.some( function( subpage ) {
                if ( subpage.data.subpage.hasOwnProperty( 'contentId' ) && subpage.data.subpage.contentId === sectionId ) {
                    foundStateId = subpage.name;
                    return true;
                }
            });

            return foundStateId;
        }
    }
})();
